/**
 * Boolean Node-Flag Filter Definition
 *
 * Filters nodes by a boolean flag carried on the node itself, rather than by an
 * edge. The slim catalog index (GET /api/graph/index) stamps these flags
 * server-side for facts the client cannot derive on its own — the first is
 * `hasSvaras`, which is computed from the LYRICS node that the slim index
 * deliberately drops before it reaches the browser.
 *
 * One definition covers every flag: the filter VALUE is the flag's field name,
 * and the operator says whether it must be set (Is) or unset (NotIs).
 *
 * Deliberate design points:
 *   - An absent flag is NOT a match, and no operator falls through to
 *     "return true". A node that predates the server change simply does not
 *     match, so the page degrades to an empty list rather than to the whole
 *     library. (Compare edgeFilter's HasAny/HasNone, which do fall through.)
 *   - Only a real `true` counts. 'yes', 1 and '' are all treated as unset, so a
 *     sloppy payload can never widen the result set.
 */

import { GraphDataNode } from '../../types/graph.types';
import {
  FilterDefinition,
  FilterOperator,
  FilterValueType,
  FilterSuggestion,
  FilterValue,
} from '../types';

/** Bhajans whose lyrics carry svara notation. Stamped by the slim index. */
export const HAS_SVARAS_FLAG = 'hasSvaras';

/** Pill labels, so a filter never renders its raw field name to a user. */
export const FLAG_LABELS: Record<string, string> = {
  [HAS_SVARAS_FLAG]: 'Svaras',
};

const flagLabel = (flag: string): string => FLAG_LABELS[flag] ?? flag;

/**
 * Read a boolean flag off a node. Accepts it at the top level (the slim index
 * shape, a sibling of `edges`/`domains`) or nested under `data`, so the web
 * side is not coupled to exactly where the server puts it.
 */
function nodeFlagIsSet(node: GraphDataNode, flag: string): boolean {
  if ((node as unknown as Record<string, unknown>)[flag] === true) return true;
  return (node.data as Record<string, unknown> | undefined)?.[flag] === true;
}

export const flagFilter: FilterDefinition = {
  id: 'flag',
  label: 'Bhajan Flag',
  shortLabel: 'Has',
  icon: 'CheckBadgeIcon',
  valueType: FilterValueType.Boolean,
  supportedOperators: [FilterOperator.Is, FilterOperator.NotIs],
  defaultOperator: FilterOperator.Is,
  placeholder: 'Select a flag...',

  suggestMatch(query: string, nodes: GraphDataNode[]): FilterSuggestion[] {
    const q = query.toLowerCase().trim();
    if (!q) return [];

    const suggestions: FilterSuggestion[] = [];
    for (const flag of Object.keys(FLAG_LABELS)) {
      const label = flagLabel(flag);
      const terms = [label.toLowerCase(), flag.toLowerCase()];

      let score = 0;
      for (const term of terms) {
        if (term === q) { score = 100; break; }
        if (term.startsWith(q)) score = Math.max(score, 80);
        else if (term.includes(q)) score = Math.max(score, 50);
      }
      if (score === 0) continue;

      const withFlag = nodes.filter((n) => nodeFlagIsSet(n, flag)).length;
      if (withFlag > 0) {
        suggestions.push({
          filterId: 'flag',
          label: `Has: ${label}`,
          description: `${withFlag} bhajan${withFlag !== 1 ? 's' : ''} have this`,
          prefilledOperator: FilterOperator.Is,
          prefilledValue: flag,
          needsConfiguration: false,
          score,
        });
      }
      const withoutFlag = nodes.length - withFlag;
      if (withoutFlag > 0 && score > 60) {
        suggestions.push({
          filterId: 'flag',
          label: `Missing: ${label}`,
          description: `${withoutFlag} bhajan${withoutFlag !== 1 ? 's' : ''} don't have this`,
          prefilledOperator: FilterOperator.NotIs,
          prefilledValue: flag,
          needsConfiguration: false,
          score: score - 10,
        });
      }
    }
    return suggestions.sort((a, b) => b.score - a.score);
  },

  execute(node: GraphDataNode, operator: FilterOperator, value: FilterValue): boolean {
    const flag = String(value);
    switch (operator) {
      case FilterOperator.Is:
        return nodeFlagIsSet(node, flag);
      case FilterOperator.NotIs:
        return !nodeFlagIsSet(node, flag);
      default:
        // Not reachable via supportedOperators. Match nothing rather than
        // everything, so an unknown operator can never silently widen a list.
        return false;
    }
  },

  getDisplayValue(value: FilterValue): string {
    return flagLabel(String(value));
  },
};
